import type { QueryClient, QueryKey } from '@tanstack/query-core'

import { persistedCollectionOptions } from '@tanstack/browser-db-sqlite-persistence'
import { queryCollectionOptions } from '@tanstack/query-db-collection'
import { createCollection } from '@tanstack/react-db'

import type { DbSession } from './db-session.ts'

type Config<TItem extends object, TKey extends number | string> = {
  getKey: (_item: TItem) => TKey
  id: string
  queryClient: QueryClient
  queryFn: () => Promise<TItem[]>
  queryKey: QueryKey
  // Bump when the item shape changes, so rows stored by an older build are dropped
  schemaVersion: number
  staleTime?: number
}

type SelectedConfig<
  TItem extends object,
  TKey extends number | string,
  TQueryData extends object,
> = {
  getKey: (_item: TItem) => TKey
  id: string
  queryClient: QueryClient
  queryFn: () => Promise<TQueryData>
  queryKey: QueryKey
  // Bump when the item shape changes, so rows stored by an older build are dropped
  schemaVersion: number
  select: (_data: TQueryData) => TItem[]
  staleTime?: number
}

/**
 * A collection synced from a REST query and, where the session has a database, stored in it:
 * rows are on screen from SQLite before the request returns.
 */
export const createSessionCollection = <TItem extends object, TKey extends number | string>(
  session: DbSession,
  { id, schemaVersion, ...query }: Config<TItem, TKey>,
) => {
  const options = queryCollectionOptions(query)

  if (!session.persistence) return createCollection(options)

  return createCollection(
    // Without explicit generics the schema type is inferred as "any schema" instead of "none"
    persistedCollectionOptions<TItem, TKey, never, NonNullable<typeof options.utils>>({
      ...options,
      id,
      persistence: session.persistence,
      schemaVersion,
    }),
  )
}

/**
 * Like createSessionCollection, but materializes a private collection-row shape from a raw Query
 * response. Query still stores the raw response; only the collection receives select's rows.
 */
export const createSelectedSessionCollection = <
  TItem extends object,
  TKey extends number | string,
  TQueryData extends object,
>(
  session: DbSession,
  { id, schemaVersion, select, ...query }: SelectedConfig<TItem, TKey, TQueryData>,
) => {
  const options = queryCollectionOptions<
    TItem,
    () => Promise<object>,
    Error,
    QueryKey,
    TKey,
    TQueryData
  >({
    ...query,
    select,
  })

  if (!session.persistence) return createCollection(options)

  return createCollection(
    // Without explicit generics the schema type is inferred as "any schema" instead of "none"
    persistedCollectionOptions<TItem, TKey, never, NonNullable<typeof options.utils>>({
      ...options,
      id,
      persistence: session.persistence,
      schemaVersion,
    }),
  )
}
